
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bug } from "lucide-react";

interface TimelineAdDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  link: string;
  onImpression: () => void;
  debug?: boolean;
}

const mockAdContent = {
  imageUrl: "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png", 
  link: "https://example.com/mock-ad"
};

// Local storage keys
const AD_SHOWN_KEY = "ad_shown";
const AD_SHOWN_TIMESTAMP_KEY = "ad_shown_timestamp";

// Empty ad URL that should be ignored
const EMPTY_AD_URL = "https://advertising-server.amadeus.net/whitepix.jpg";

// Check if the ad should be shown based on localStorage data
// Returns true if the ad should be shown, false otherwise
const shouldShowAd = (): boolean => {
  const adShown = localStorage.getItem(AD_SHOWN_KEY) === "true";
  const adShownTimestamp = Number(localStorage.getItem(AD_SHOWN_TIMESTAMP_KEY) || "0");
  
  if (!adShown) {
    return true; // Ad has never been shown
  }
  
  // Check if 48 hours have passed since the last impression
  const now = Date.now();
  const hoursPassed = (now - adShownTimestamp) / (1000 * 60 * 60);
  
  return hoursPassed >= 48;
};

// Check if the ad is a valid ad (not an empty ad)
const isValidAd = (imageUrl: string): boolean => {
  return imageUrl !== EMPTY_AD_URL;
};

// Mark the ad as shown in localStorage
const markAdAsShown = (): void => {
  try {
    localStorage.setItem(AD_SHOWN_KEY, "true");
    localStorage.setItem(AD_SHOWN_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    localStorage.clear();
  }
};

export const TimelineAdDialog = ({
  open,
  onOpenChange,
  imageUrl,
  link,
  onImpression,
  debug
}: TimelineAdDialogProps) => {
  useEffect(() => {
    // Check if this is a valid ad (not an empty whitepix image)
    const isValid = debug || isValidAd(imageUrl);
    const canShowAd = debug ? true : (shouldShowAd() && isValid);
    
    console.debug('🎯 Ad Dialog State:', {
      isOpen: open,
      imageUrl: imageUrl?.substring(0, 50) + '...',
      link: link?.substring(0, 50) + '...',
      isDebug: debug,
      isValidAd: isValid,
      canShowAd: canShowAd,
      adShownInLocalStorage: localStorage.getItem(AD_SHOWN_KEY) === "true",
      lastShownTimestamp: new Date(Number(localStorage.getItem(AD_SHOWN_TIMESTAMP_KEY) || "0")).toISOString()
    });
    
    // If the ad is invalid and not in debug mode, close the dialog
    if (!isValid && !debug && open) {
      console.debug('🚫 Closing ad dialog - invalid ad detected (whitepix)');
      onOpenChange(false);
      return;
    }
    
    if (open) {
      console.debug('📢 Triggering ad impression');
      onImpression();
      
      // Store in localStorage that the ad was shown (except in debug mode)
      if (!debug && isValid) {
        markAdAsShown();
        console.debug('📝 Ad impression recorded in localStorage with timestamp:', new Date().toISOString());
      }
    }
  }, [open, imageUrl, link, onImpression, debug, onOpenChange]);

  const finalImageUrl = debug ? mockAdContent.imageUrl : imageUrl;
  const finalLink = debug ? mockAdContent.link : link;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[320px] p-0">
          <a
            href={finalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative"
            onClick={() => console.debug('🔗 Ad link clicked')}
          >
            <img
              src={finalImageUrl}
              alt="Advertisement"
              className="w-full h-auto"
              width={320}
              height={480}
              onLoad={() => console.debug('🖼️ Ad image loaded successfully')}
              onError={() => console.error('❌ Ad image failed to load')}
            />
            {debug && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <p className="text-white text-2xl font-bold text-center px-4">
                  Discover our incredible offer!
                </p>
              </div>
            )}
          </a>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Debug component to trigger mock ad
export const TimelineAdDebugButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      size="icon"
      variant="outline"
      className="fixed bottom-20 right-4 z-50 rounded-full"
      onClick={onClick}
    >
      <Bug className="h-4 w-4" />
    </Button>
  );
};
